/**
 * Tiny JWT helpers — we only need the username and (optional) email/role from
 * the access token to render the sidebar footer. No signature verification:
 * the backend already gates every request, this is presentation only.
 */

interface JwtPayload {
  username?: string;
  user_id?: number | string;
  email?: string;
  exp?: number;
  [key: string]: unknown;
}

function base64UrlDecode(input: string): string {
  const pad = input.length % 4 === 0 ? '' : '='.repeat(4 - (input.length % 4));
  const b64 = (input + pad).replace(/-/g, '+').replace(/_/g, '/');
  try {
    return decodeURIComponent(
      atob(b64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
  } catch {
    return atob(b64);
  }
}

export function decodeJwt(token: string | null): JwtPayload | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    return JSON.parse(base64UrlDecode(parts[1])) as JwtPayload;
  } catch {
    return null;
  }
}

export function getCurrentUsername(): string | null {
  // Prefer the username typed at login (saved to localStorage by LoginPage),
  // fall back to the JWT payload if a future backend includes it.
  const stored = localStorage.getItem('username');
  if (stored) return stored;
  const payload = decodeJwt(localStorage.getItem('jwt'));
  if (!payload) return null;
  return (payload.username as string | undefined) ?? null;
}

export function isJwtExpired(token: string | null): boolean {
  const payload = decodeJwt(token);
  if (!payload || typeof payload.exp !== 'number') return false;
  return payload.exp * 1000 < Date.now();
}

export function computeInitials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return name.slice(0, 1).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
